import React, { useEffect, useState, useRef, useMemo } from "react";
import { Marker, InfoWindow } from "@react-google-maps/api";
import PropTypes from "prop-types";
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';

const VehicleMarkers = ({ vehicles, selectedVehicle, onSelectVehicle, theme }) => {
  const [activeMarker, setActiveMarker] = useState(null);
  const [copySuccess, setCopySuccess] = useState('');
  const { copyToClipboard } = useCopyToClipboard();
  
  const isMounted = useRef(true);
  const markersRef = useRef({});

  // 🔧 SOLUCIÓN: Generar clave única que incluya proveedor
  const generateUniqueKey = (vehicle) => {
    const provider = vehicle.provider || vehicle.Provider || 'unknown';
    const vehicleId = vehicle.UnitId || vehicle.UnitPlate || vehicle.GpsIdentif || vehicle.Imei;
    const fallbackId = `${vehicle.Latitude}_${vehicle.Longitude}_${Date.now()}`;
    
    return `${provider}_${vehicleId || fallbackId}`;
  };

  // 🚀 OPTIMIZACIÓN: Filtrar y deduplicar vehículos con useMemo
  const processedVehicles = useMemo(() => {
    if (!vehicles || vehicles.length === 0) return [];
    
    const vehicleMap = new Map();
    const validVehicles = [];

    vehicles.forEach((vehicle) => {
      const lat = parseFloat(vehicle.Latitude || vehicle.Latitud);
      const lng = parseFloat(vehicle.Longitude || vehicle.Longitud);
      
      // Validar coordenadas
      if (isNaN(lat) || isNaN(lng)) return;
      
      const uniqueKey = generateUniqueKey(vehicle);
      
      // Evitar duplicados exactos (mismo proveedor + mismo vehículo)
      if (!vehicleMap.has(uniqueKey)) {
        vehicleMap.set(uniqueKey, {
          ...vehicle,
          _uniqueKey: uniqueKey,
          _position: { lat, lng }
        });
        validVehicles.push(vehicleMap.get(uniqueKey));
      } else {
        // Si existe, mantener el más reciente
        const existing = vehicleMap.get(uniqueKey);
        const existingDate = new Date(existing.ReportDate || existing.lastUpdate || 0);
        const currentDate = new Date(vehicle.ReportDate || vehicle.lastUpdate || 0);
        
        if (currentDate > existingDate) {
          vehicleMap.set(uniqueKey, {
            ...vehicle,
            _uniqueKey: uniqueKey,
            _position: { lat, lng }
          });
          // Actualizar en el array
          const index = validVehicles.findIndex(v => v._uniqueKey === uniqueKey);
          if (index !== -1) {
            validVehicles[index] = vehicleMap.get(uniqueKey);
          }
        }
      }
    });
    return validVehicles;
  }, [vehicles]);

  // Limpieza al desmontar
  useEffect(() => {
    return () => {
      isMounted.current = false;
      setActiveMarker(null);
      markersRef.current = {};
    };
  }, []);

  // Sincronización del marcador activo
  useEffect(() => {
    if (selectedVehicle && isMounted.current) {
      const vehicleKey = generateUniqueKey(selectedVehicle);
      setActiveMarker(vehicleKey);
    } else {
      setActiveMarker(null);
    }
  }, [selectedVehicle]);

  // Manejo de clicks optimizado
  const handleMarkerClick = (vehicle) => {
    if (!isMounted.current) return;
    
    const vehicleKey = vehicle._uniqueKey;
    
    if (markersRef.current[vehicleKey]) {
      onSelectVehicle(vehicle);
      setActiveMarker(vehicleKey);
    }
  };

  // Registro de marcadores
  const registerMarker = (vehicleKey, markerInstance) => {
    if (markerInstance && isMounted.current) {
      markersRef.current[vehicleKey] = markerInstance;
    }
  };

  // Funciones existentes (sin cambios)
  const handleCopyCoordinates = (vehicle) => {
    const coords = `${vehicle._position.lat}, ${vehicle._position.lng}`;
    copyToClipboard(coords);
    setCopySuccess('Coordenadas copiadas');
    setTimeout(() => setCopySuccess(''), 2000);
  };

  const handleCopyLocation = (vehicle) => {
    const location = vehicle.Domicilio || vehicle.Direccion || 'Ubicación no disponible';
    copyToClipboard(location);
    setCopySuccess('Ubicación copiada');
    setTimeout(() => setCopySuccess(''), 2000);
  };

  const getVehicleIcon = (vehicle) => {
    const isActive = (vehicle.GpsSpeed && parseFloat(vehicle.GpsSpeed) > 0) || vehicle.Ignition === 1;
    const speed = parseFloat(vehicle.GpsSpeed || 0);
    
    let iconColor = '#666666'; // Inactivo
    if (isActive) {
      iconColor = speed > 60 ? '#ff4444' : speed > 30 ? '#ffaa00' : '#44ff44';
    }
    
    return {
      path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
      fillColor: iconColor,
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 2,
      scale: 1.2,
      anchor: { x: 12, y: 24 }
    };
  };

  // 🎯 RENDERIZADO OPTIMIZADO
  const renderMarkers = () => {
    if (!processedVehicles.length) return null;

    return processedVehicles.map(vehicle => {
      const uniqueKey = vehicle._uniqueKey;
      
      return (
        <Marker
          key={uniqueKey} // 🔧 Usar clave única
          position={vehicle._position}
          icon={getVehicleIcon(vehicle)}
          onClick={() => handleMarkerClick(vehicle)}
          onLoad={(marker) => registerMarker(uniqueKey, marker)}
          animation={
            selectedVehicle && generateUniqueKey(selectedVehicle) === uniqueKey 
              ? window.google?.maps?.Animation?.BOUNCE 
              : null
          }
          title={vehicle.UnitId || vehicle.UnitPlate || 'Vehículo'}
        >
          {activeMarker === uniqueKey && markersRef.current[uniqueKey] && (
            <InfoWindow
              onCloseClick={() => {
                if (isMounted.current) {
                  setActiveMarker(null);
                }
              }}
              anchor={markersRef.current[uniqueKey]}
            >
              <div className={`GM__${theme}-info-window`}>
                <div className={`GM__${theme}-info-header`}>
                  <h4>{vehicle.UnitId || vehicle.UnitPlate}</h4>
                  <span className={`GM__${theme}-provider-badge`}>
                    {vehicle.provider || vehicle.Provider}
                  </span>
                </div>
                
                <div className={`GM__${theme}-info-content`}>
                  <p><strong>Velocidad:</strong> {vehicle.GpsSpeed || 0} km/h</p>
                  <p><strong>Dirección:</strong> {vehicle.Direction || 'N/A'}</p>
                  <p><strong>Última actualización:</strong> {
                    new Date(vehicle.ReportDate || vehicle.lastUpdate).toLocaleString('es-CO')
                  }</p>
                  
                  {vehicle.Domicilio && (
                    <p><strong>Ubicación:</strong> {vehicle.Domicilio}</p>
                  )}
                </div>
                
                <div className={`GM__${theme}-info-actions`}>
                  <button onClick={() => handleCopyCoordinates(vehicle)}>
                    Copiar Coordenadas
                  </button>
                  <button onClick={() => handleCopyLocation(vehicle)}>
                    Copiar Ubicación
                  </button>
                </div>
                
                {copySuccess && (
                  <div className={`GM__${theme}-copy-success`}>
                    {copySuccess}
                  </div>
                )}
              </div>
            </InfoWindow>
          )}
        </Marker>
      );
    });
  };

  return renderMarkers();
};

VehicleMarkers.propTypes = {
  vehicles: PropTypes.array.isRequired,
  selectedVehicle: PropTypes.object,
  onSelectVehicle: PropTypes.func.isRequired,
  theme: PropTypes.string.isRequired
};

export default VehicleMarkers;